import React, { useState } from "react";
import NotificationStatusCard from "@/components/NotificationStatusCard";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import InstallAppCard from "@/components/InstallAppCard";
import { Button } from "@/components/ui/button";
import { Gear, Key, Bell as BellIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * v1.27.67 — Customer Settings.
 * Now houses:
 *   • Notifications (existing NotificationStatusCard)
 *   • Change Password (moved here from the top-right header button)
 *   • Quick link to the in-app notifications inbox
 * v1.27.72 — Added an always-visible Install App card so parties can
 * trigger the PWA install flow from Settings whenever they want,
 * independent of Chrome's automatic banner heuristics.
 */
export default function CustomerSettings() {
    const [pwOpen, setPwOpen] = useState(false);
    return (
        <div className="space-y-6" data-testid="customer-settings-page">
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                    <Gear size={20} weight="bold" />
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Preferences</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Settings</h1>
                </div>
            </div>

            <InstallAppCard />

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                    <BellIcon size={18} weight="duotone" className="text-slate-500" />
                    Notifications
                </h2>
                <NotificationStatusCard />
                <div className="mt-3">
                    <Link to="/portal/notifications" className="text-xs font-semibold text-blue-700 hover:underline" data-testid="settings-open-notifications-inbox">
                        Open notifications inbox →
                    </Link>
                    {" · "}
                    <Link to="/portal/notifications/diag" className="text-xs font-semibold text-indigo-700 hover:underline" data-testid="settings-open-notification-diag">
                        Notification diagnostics →
                    </Link>
                </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                    <Key size={18} weight="duotone" className="text-slate-500" />
                    Security
                </h2>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-medium text-slate-800">Password</div>
                        <div className="text-xs text-slate-500">Update your login password. Minimum 6 characters.</div>
                    </div>
                    <Button
                        data-testid="settings-change-password-btn"
                        onClick={() => setPwOpen(true)}
                        className="bg-yellow-600 text-white hover:bg-yellow-700"
                    >
                        <Key size={16} className="mr-2" /> Change Password
                    </Button>
                </div>
            </section>

            <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        </div>
    );
}
